import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DatabaseImport = () => {
  const [status, setStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      const response = await axios.get(`${API}/api/admin/database-status`, getAuthHeaders());
      setStatus(response.data);
      toast.success('État de la base de données récupéré');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Non autorisé. Veuillez vous connecter en tant qu\'admin.');
        navigate('/login');
      } else {
        toast.error('Erreur lors de la vérification du statut');
      }
      console.error(error);
    } finally {
      setChecking(false);
    }
  };

  const importDatabase = async () => {
    if (!window.confirm('⚠️ ATTENTION : Cela va REMPLACER toutes les données actuelles de la base de données. Êtes-vous sûr de vouloir continuer ?')) {
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(`${API}/api/admin/import-database`, {}, getAuthHeaders());
      toast.success('Base de données importée avec succès !', { duration: 5000 });
      setStatus(null);
      
      // Rafraîchir le statut après l'import
      setTimeout(() => checkStatus(), 1000);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Non autorisé. Veuillez vous connecter en tant qu\'admin.');
        navigate('/login');
      } else if (error.response?.status === 404) {
        toast.error('Fichier d\'export introuvable');
      } else {
        toast.error('Erreur lors de l\'import');
      }
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen p-8" style={{ background: 'linear-gradient(135deg, #F5F5F5 0%, #E8E4D9 100%)' }}>
      <div className="max-w-4xl mx-auto">
        <div className="card mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#335C2E' }}>
            🔄 Import de Base de Données
          </h1>
          <p className="text-sm" style={{ color: '#666666' }}>
            Page temporaire pour importer la base de données après redéploiement
          </p>
        </div>

        <div className="card mb-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#333333' }}>
            1️⃣ Vérifier l'état actuel
          </h2>
          <button
            onClick={checkStatus}
            disabled={checking}
            className="btn-secondary mb-4"
          >
            {checking ? 'Vérification...' : 'Vérifier le Statut de la BD'}
          </button>

          {status && (
            <div className="mt-4 p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
              <h3 className="font-semibold mb-2" style={{ color: '#335C2E' }}>État Actuel de la Base de Données :</h3>
              <ul className="space-y-1 text-sm" style={{ color: '#666666' }}>
                <li>• Settings: {status.collections.settings} document(s)</li>
                <li>• Admin Users: {status.collections.admin_users} utilisateur(s)</li>
                <li>• Submissions: {status.collections.submissions} soumission(s)</li>
                <li>• Appointments: {status.collections.appointments} rendez-vous</li>
                {status.collections.segments_count !== undefined && (
                  <li>• Segments: {status.collections.segments_count} segment(s)</li>
                )}
                {status.collections.options_count !== undefined && (
                  <li>• Options: {status.collections.options_count} option(s)</li>
                )}
                <li className="mt-2">
                  • Fichier d'export présent: {status.export_file_exists ? '✅ Oui' : '❌ Non'}
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#333333' }}>
            2️⃣ Importer la nouvelle base de données
          </h2>
          
          <div className="p-4 rounded-lg border-l-4 mb-4" style={{ background: '#FFF8E1', borderColor: '#F59E0B' }}>
            <p className="text-sm font-medium" style={{ color: '#92400E' }}>
              ⚠️ <strong>Attention :</strong> Cette action va REMPLACER toutes les données actuelles (settings, segments, options, soumissions, rendez-vous) par les données exportées de la version preview.
            </p>
          </div>

          <button
            onClick={importDatabase}
            disabled={importing || !status?.export_file_exists}
            className="btn-primary"
          >
            {importing ? 'Import en cours...' : '📥 Importer la Base de Données'}
          </button>

          {!status?.export_file_exists && status && (
            <p className="text-sm mt-2" style={{ color: '#DC2626' }}>
              ❌ Le fichier d'export n'est pas présent sur le serveur
            </p>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/admin')}
            className="text-sm"
            style={{ color: '#335C2E' }}
          >
            ← Retour au Panel Admin
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseImport;
